import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { CaseWorkerRole } from '@repo/shared/dtos/caseWorker'
import { DataStatus } from '@repo/ui/utils'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import { caseWorkerFields } from '@/store/spi/api'
import { CaseWorker } from '@/store/user'

export const caseWorker = {
  admin: {
    invite: createAsyncThunk<
      { caseWorker: CaseWorker },
      {
        email: string
        firstName: string
        lastName: string
        role: CaseWorkerRole
      },
      AsyncThunkConfig
    >(
      'INVITE_CASE_WORKER',
      async (fields, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const caseWorker = await api.admin.caseWorkers.inviteOne({
          organizationId,
          fields,
        })
        if (hasError(caseWorker)) return rejectWithValue(handleError(caseWorker))
        return fulfillWithValue({ caseWorker })
      },
    ),
    disableOrEnable: createAsyncThunk<
      { caseWorker: CaseWorker },
      { caseWorkerId: string; disabled: boolean },
      AsyncThunkConfig
    >(
      'DISABLE_OR_ENABLE_CASE_WORKER',
      async (
        { caseWorkerId, disabled },
        { extra, fulfillWithValue, rejectWithValue, getState },
      ) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const caseWorker = await api.admin.caseWorkers.disableOrEnableOne({
          organizationId,
          caseWorkerId,
          disabled,
        })
        if (hasError(caseWorker)) return rejectWithValue(handleError(caseWorker))
        return fulfillWithValue({ caseWorker })
      },
    ),
    update: createAsyncThunk<
      { caseWorker: CaseWorker },
      { caseWorkerId: string; fields: caseWorkerFields },
      AsyncThunkConfig
    >(
      'UPDATE_CASE_WORKER',
      async ({ caseWorkerId, fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const caseWorker = await api.admin.caseWorkers.patchOne({
          caseWorkerId,
          organizationId,
          fields,
        })
        if (hasError(caseWorker)) return rejectWithValue(handleError(caseWorker))
        return fulfillWithValue({ caseWorker })
      },
    ),
  },

  app: {
    updateCurrent: createAsyncThunk<{ caseWorker: CaseWorker }, caseWorkerFields, AsyncThunkConfig>(
      'UPDATE_CURRENT_CASE_WORKER',
      async (fields, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
        const caseWorker = getState().user.caseWorker
        if (!DataStatus.isFulfilled(caseWorker)) return rejectWithValue(RejectValue.NoCaseWorker)

        // FIXME: when available from backend
        //  const res = await api.me.patch({
        //   organizationId,
        //   fields,
        // })
        const user = await api.auth.patchCurrent({
          fields,
        })
        if (hasError(user)) return rejectWithValue(handleError(user))
        const newCaseWorker = await api.caseWorkers.getOne({
          caseWorkerId: caseWorker.id,
          organizationId,
        })
        if (hasError(newCaseWorker)) return rejectWithValue(handleError(newCaseWorker))
        return fulfillWithValue({
          caseWorker: {
            ...newCaseWorker,
            firstName: user.firstName,
            lastName: user.lastName,
          },
        })
      },
    ),
  },
  sudo: {
    updateRole: createAsyncThunk<
      { caseWorker: CaseWorker },
      {
        organizationId: string
        caseWorkerId: string
        role: CaseWorkerRole
      },
      AsyncThunkConfig
    >(
      'SUDO_UPDATE_CASE_WORKER_ROLE',
      async (
        { caseWorkerId, role, organizationId },
        { extra, fulfillWithValue, rejectWithValue },
      ) => {
        const { api } = extra
        const caseWorker = await api.admin.caseWorkers.patchOne({
          caseWorkerId,
          organizationId,
          fields: { role },
        })
        if (hasError(caseWorker)) return rejectWithValue(handleError(caseWorker))
        return fulfillWithValue({ caseWorker })
      },
    ),
  },
}
